import React, { useEffect } from "react";
import { Link } from "react-router-dom";

const Section = ({ title, children }) => (
  <div className="mb-8">
    <h2 className="font-serif text-2xl text-[#14140f] mb-2">{title}</h2>
    <div className="text-[15px] text-[#14140f]/85 leading-7 space-y-2">{children}</div>
  </div>
);

export default function Privacy() {
  useEffect(() => {
    document.title = "Privacy — Living Wisdom Explorer";
  }, []);

  return (
    <div className="max-w-3xl mx-auto px-6 py-16" data-testid="privacy-page">
      <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#0a7d5f] mb-4">
        Living Wisdom Explorer
      </p>
      <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-[#14140f] mb-3">
        Privacy Policy
      </h1>
      <p className="text-sm text-[#14140f]/50 mb-10">Last updated: June 2026</p>

      <Section title="Who we are">
        <p>
          Living Wisdom Explorer is an educational platform for exploring ancient Indian
          philosophy. This policy explains what personal information we collect, why, and the choices
          you have.
        </p>
      </Section>

      <Section title="What we collect">
        <p>You can browse and search the knowledge graph without an account. If you choose to sign in with Google, we collect and store:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Your <strong>name</strong>, <strong>email address</strong>, and <strong>profile picture</strong> from your Google account.</li>
          <li>Your <strong>learning activity</strong> — the entities you mark as learned and any personal <strong>notes</strong> you save.</li>
          <li>Basic technical data (e.g. session tokens) needed to keep you signed in.</li>
        </ul>
      </Section>

      <Section title="How we use it">
        <p>We use this information solely to:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Sign you in and keep your session secure.</li>
          <li>Save and show your personal progress, notes and learning tracks.</li>
          <li>Manage roles (learner, curator, admin) for people who help maintain the content.</li>
        </ul>
        <p>We do <strong>not</strong> sell, rent, or share your personal information with advertisers.</p>
      </Section>

      <Section title="Third-party services">
        <p>
          We use <strong>Google Sign-In</strong> to authenticate you. When you sign in, Google shares the
          basic profile details listed above. Your use of Google Sign-In is also governed by Google's own
          privacy policy. AI-generated search answers are produced by a third-party AI model; your search
          query is sent to that provider to generate a response.
        </p>
      </Section>

      <Section title="Data retention & your choices">
        <p>
          We keep your account data for as long as your account exists. You can request access to, or
          deletion of, your personal data (account, notes and progress) at any time by contacting the site
          administrator. Signing out ends your session on this device.
        </p>
      </Section>

      <Section title="Cookies">
        <p>
          We use a single secure, http-only session cookie to keep you signed in. We do not use tracking or
          advertising cookies.
        </p>
      </Section>

      <Section title="Contact">
        <p>
          Questions about this policy or your data? Reach out to the site administrator through the channel
          where you were invited, and we'll be glad to help.
        </p>
      </Section>

      <div className="mt-10 pt-6 border-t border-[#14140f]/10">
        <Link to="/" className="text-sm text-[#c23c28] font-medium hover:underline" data-testid="privacy-back-home">
          ← Back to home
        </Link>
      </div>
    </div>
  );
}
